export interface DiffHunk {
  modifiedStartLineNumber: number;
  modifiedEndLineNumber: number;
}

export interface DiffPair {
  original: string;
  modified: string;
  language: string;
  lineCount: number;
  stats: { added: number; removed: number };
  hunks: DiffHunk[];
  /** True quando o diff representa um arquivo novo (added) — original vazio, só inserções. */
  isAddedFile: boolean;
}

// ─── extractFileDiff ──────────────────────────────────────────────────────────
// Idêntico ao da extensão VS Code: isola o bloco de diff de um arquivo específico
// dentro de um multi-file unified diff (diff --git a/... b/...).

const normalizeDiffPath = (p: string): string =>
  (p || '').replace(/^["']|["']$/g, '').replace(/^[ab]\//, '').toLowerCase();

export function extractFileDiff(diffText: string, targetPath: string): string {
  const normTarget = normalizeDiffPath(targetPath);
  const blocks: Array<{ path: string; lines: string[] }> = [];
  let current: { path: string; lines: string[] } | null = null;

  for (const line of (diffText || '').split('\n')) {
    if (line.startsWith('diff --git ')) {
      if (current) { blocks.push(current); }
      const parts = line.split(' ');
      const pathPart = normalizeDiffPath(
        parts[2]?.replace(/^a\//, '') || parts[3]?.replace(/^b\//, '') || ''
      );
      current = { path: pathPart, lines: [] };
      continue;
    }
    if (!current) {
      current = { path: normTarget || '', lines: [] };
    }
    current.lines.push(line);
  }
  if (current) { blocks.push(current); }

  if (!blocks.length) { return diffText; }

  const byExact = blocks.find(b => b.path === normTarget);
  if (byExact) { return byExact.lines.join('\n'); }

  const bySuffix = blocks.find(b => normTarget && b.path.endsWith(normTarget));
  return (bySuffix || blocks[0]).lines.join('\n');
}

// ─── parseDiffToPair ──────────────────────────────────────────────────────────
// Idêntico ao parseUnifiedDiff da extensão VS Code:
// - prefix ' ' → contexto (ambos), '-' → original, '+' → modified
// Hunks rastreiam apenas as linhas efetivamente alteradas ('+'), sem contexto,
// para que a navegação ↑↓ e seleção sejam precisas (igual ao Monaco getLineChanges).

export function parseDiffToPair(rawDiff: string, language = 'plaintext'): DiffPair {
  if (!rawDiff?.trim()) {
    return { original: '', modified: '', language, lineCount: 0, stats: { added: 0, removed: 0 }, hunks: [], isAddedFile: false };
  }

  const original: string[] = [];
  const modified: string[] = [];
  let added = 0;
  let removed = 0;
  let isAddedFile = false;
  const hunks: DiffHunk[] = [];

  // Estado do hunk atual
  let modifiedLineCounter = 0; // linha atual no lado modified (1-based)
  let hunkAnchor = -1;         // posição no modified ao entrar no hunk (para deleções puras)
  let hunkChangedStart = -1;   // primeira linha '+' do hunk
  let hunkChangedEnd = -1;     // última linha '+' do hunk

  const flushHunk = () => {
    if (hunkAnchor < 0) { return; }
    const s = hunkChangedStart > 0 ? hunkChangedStart : hunkAnchor;
    const e = hunkChangedEnd   > 0 ? hunkChangedEnd   : hunkAnchor;
    hunks.push({ modifiedStartLineNumber: s, modifiedEndLineNumber: e });
    hunkAnchor = hunkChangedStart = hunkChangedEnd = -1;
  };

  const lines = rawDiff.split('\n').map(l => (l.endsWith('\r') ? l.slice(0, -1) : l));
  // `---`/`+++` só são cabeçalho antes do primeiro hunk de cada arquivo. Dentro do hunk são
  // linha removida/adicionada cujo conteúdo começa com `--`/`++` (ex.: `++i;` adicionado).
  let inHunk = false;

  for (const line of lines) {
    if (line === '') { original.push(''); modified.push(''); modifiedLineCounter++; continue; }
    if (line.startsWith('diff ')) { inHunk = false; }

    // Detecção de arquivo novo (added): `new file mode` ou `--- /dev/null`.
    if (!inHunk && (line.startsWith('new file') || line.startsWith('--- /dev/null'))) {
      isAddedFile = true;
    }

    // Cabeçalhos — ignorar
    if (
      (!inHunk && (line.startsWith('---') || line.startsWith('+++'))) ||
      line.startsWith('diff ') ||
      line.startsWith('index ') ||
      line.startsWith('new file') ||
      line.startsWith('deleted file') ||
      line.startsWith('Binary files') ||
      line.startsWith('\\ No newline')
    ) { continue; }

    // Início de novo hunk: @@ -old +newStart,newCount @@
    if (line.startsWith('@@')) {
      inHunk = true;
      flushHunk();
      const m = line.match(/@@ -\d+(?:,\d+)? \+(\d+)(?:,\d+)? @@/);
      if (m) {
        modifiedLineCounter = parseInt(m[1], 10) - 1; // será incrementado ao processar as linhas
        hunkAnchor = parseInt(m[1], 10);
      }
      continue;
    }

    const prefix = line.charAt(0);
    const content = line.slice(1);

    if (prefix === ' ') {
      original.push(content); modified.push(content);
      modifiedLineCounter++;
    } else if (prefix === '-') {
      original.push(content);
      removed++;
      // linha removida não existe no modified — não incrementa modifiedLineCounter
    } else if (prefix === '+') {
      modified.push(content);
      added++;
      modifiedLineCounter++;
      if (hunkChangedStart < 0) { hunkChangedStart = modifiedLineCounter; }
      hunkChangedEnd = modifiedLineCounter;
    } else {
      original.push(line); modified.push(line);
      modifiedLineCounter++;
    }
  }

  flushHunk(); // fecha o último hunk

  return {
    original: original.join('\n'),
    modified: modified.join('\n'),
    language,
    lineCount: Math.max(original.length, modified.length),
    stats: { added, removed },
    hunks,
    isAddedFile: isAddedFile && removed === 0
  };
}

// ─── detectLanguage ───────────────────────────────────────────────────────────

export function detectLanguage(filePath: string): string {
  const ext = (filePath || '').split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    ts: 'typescript', js: 'javascript',
    html: 'html', htm: 'html',
    css: 'css', scss: 'scss', less: 'less',
    json: 'json', xml: 'xml', sql: 'sql',
    py: 'python', java: 'java', cs: 'csharp',
    cpp: 'cpp', c: 'cpp', h: 'cpp',
    // AdvPL / TLPP (registrado como linguagem custom no Monaco do Smart Review)
    prw: 'advpl', prx: 'advpl', tlpp: 'advpl',
    prg: 'advpl', aph: 'advpl', apl: 'advpl',
    apw: 'advpl',
    th: 'advpl', ch: 'advpl',
    yaml: 'yaml', yml: 'yaml',
    sh: 'shell', bash: 'shell',
    md: 'markdown',
  };
  return map[ext] ?? 'plaintext';
}
